import React, { useState } from 'react';
import AddEventController from '../../merode/controllers/AddEventController';
import { RSButton, RSFormLabel, RSInput } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import Styles from '../../styles/colors';
import { useController, useAsynchronousAction } from '../../hooks';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IModelVersion } from '../../stores/projects/model_version';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSInputChange } from '../Inspector/rsForm';

interface AddEventFormProps {
  /** The ModelVersion to add the event to. */
  modelVersion: IModelVersion;
  /** The id of the owner event type in case of a specialised event. */
  ownerEventType?: string;
  /** Callback to cancel adding an event. */
  onCancel?: () => void;
  /** Callback to send the report up. */
  onReport?: (report: Report) => void;
}

/**
 * Form to add a specialised event type.
 *
 * Props:
 * - modelVersion: the ModelVersion to add the event to.
 * - ownerEventType: the id of the owner event type in case of a specialised event.
 * - onCancel: callback to cancel adding an event.
 * - onReport: callback to send the report up.
 */
function AddEventForm({ modelVersion, ownerEventType, onCancel, onReport }: AddEventFormProps) {
  // Store `IModelVersion` is the same loaded node the controller's narrower
  // `IModelVersion` describes; cast at the construction boundary only.
  const controller = useController(
    () => new AddEventController(modelVersion as unknown as IControllerModelVersion)
  );

  // Whether we are adding a specialised event (was a getter on the class).
  const specialised = ownerEventType != null;

  // These don't change for the life-time of the controller; the class cached
  // them as instance fields in the constructor. Compute them once on first
  // render and keep them stable. They are only meaningful when specialised.
  // `ownerEventType` is non-null whenever `specialised` is true (it is the guard);
  // the `!` is type-only and runtime-identical to the original guarded reads.
  const [ownerEventTypeName] = useState(() =>
    specialised ? controller.eventName(ownerEventType!) : undefined
  );
  const [inheritanceOptions] = useState(() =>
    specialised ? controller.inheritances(ownerEventType!) : []
  );

  // Initial state mirrors the constructor's two branches.
  const [name, setName] = useState(() =>
    specialised ? controller.suggestName(ownerEventType!) : ''
  );
  const [inheritance, setInheritance] = useState<string | undefined>(() =>
    specialised && inheritanceOptions.length > 0 ? inheritanceOptions[0].value : undefined
  );

  // The async add flow. busy reproduces the `working` flag (true while the
  // promise is pending), and onReport fires on success — identical timing to the
  // class's setState({working:true}) -> await -> setState({working:false}) ->
  // onReport(report).
  // `addSpecialisedEvent`/`addEvent` accept `OrId`/`string`; the action's args
  // flow through `execute` (`unknown[]`). Pass them as the controller methods
  // expect — `args[0]`/`args[1]` are the name and (when specialised) inheritance
  // id, exactly as the original JS. The hook forwards the resolved `Report` as
  // `unknown`; cast it in the success callback.
  const addAction = useAsynchronousAction(
    (...args: unknown[]) =>
      specialised
        ? controller.addSpecialisedEvent(
            args[0] as string,
            ownerEventType!,
            args[1] as string
          )
        : controller.addEvent(args[0] as string),
    (report) => {
      if (onReport) {
        onReport(report as Report);
      }
    }
  );
  const working = addAction.busy;

  const nameCheck = controller.checkNameInput(name);
  const nameOk = name !== '' && nameCheck.ok;
  const valid = specialised ? nameOk && inheritance != null : nameOk;

  /**
   * Actually add the event
   */
  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();

    if (valid) {
      if (specialised) {
        addAction.execute(name, inheritance);
      } else {
        addAction.execute(name);
      }
    }
  };

  const onChangeInheritance = (next: MerlinSelectOption | null) => {
    if (next != null) {
      setInheritance(next.value as string);
    }
  };

  const renderInheritanceSelector = () => {
    if (specialised) {
      const classNames = ['rs-element', 'rs-input'];
      if (inheritance == null) {
        classNames.push('rs-input--danger');
      }
      return (
        <div className="row">
          <div className="col">
            <div className={classNames.join(' ')}>
              <RSFormLabel>Inheritance: </RSFormLabel>
              <MerlinSelect
                className="rs-input-wrap"
                options={inheritanceOptions}
                value={inheritance}
                onChange={onChangeInheritance}
                searchable={false}
                clearable={false}
                disabled={working}
              />
              {inheritance == null && (
                <span className="rs-input__text">
                  <p>Inheritance is required.</p>
                </span>
              )}
            </div>
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <div className="merlin-modal add-event-form">
      <div className="title">Add {specialised ? 'specialised ' : ''}event</div>
      <form className="form" onSubmit={onAdd}>
        <div className="modal-body">
          <div className="container">
            {specialised && (
              <div className="row">
                <div className="col">Specialises event {ownerEventTypeName}.</div>
              </div>
            )}
            <div className="row">
              <div className="col">
                <RSInput
                  autoFocus
                  name="name"
                  label="Event name"
                  value={name}
                  onChange={asRSChange<RSInputChange>((value: string) => setName(value))}
                  disabled={working}
                  level={rsValue(nameCheck.ok ? null : 'danger')}
                  text={rsValue(nameCheck.ok ? null : nameCheck.message)}
                />
              </div>
            </div>
            {renderInheritanceSelector()}
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            type="button"
            value="Cancel"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onCancel}
          />
          <RSButton
            type="submit"
            background={Styles.blueGradient}
            value={rsValue(working ? null : 'Add')}
            disabled={!valid}
            iconName={rsValue(working ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(working ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
}

export default AddEventForm;
